/**
 * Canonical CodeStitch theme token definitions used by the builder UI,
 * renderer, migration, and export paths.
 */

import { contrastRatio } from './wcag-contrast.js';

const BASE_THEME_COLORS = Object.freeze({
  '--primary': '#ff6a3e',
  '--primaryLight': '#ffba43',
  '--secondary': '#ffba43',
  '--secondaryLight': '#ffd675',
  '--headerColor': '#1a1a1a',
  '--bodyTextColor': '#4e4b66',
  '--bodyTextColorWhite': '#fafbfc'
});

export const THEME_COLOR_DEFS = Object.freeze([
  { key: '--primary', label: 'Primary', description: 'Buttons, links, accents', group: 'core' },
  {
    key: '--primaryLight',
    label: 'Primary Light',
    description: 'Hover states, highlights',
    group: 'core'
  },
  { key: '--secondary', label: 'Secondary', description: 'Secondary accents', group: 'core' },
  {
    key: '--secondaryLight',
    label: 'Secondary Light',
    description: 'Secondary highlights',
    group: 'core'
  },
  {
    key: '--headerColor',
    label: 'Header Color',
    description: 'Headings text color',
    group: 'core'
  },
  {
    key: '--bodyTextColor',
    label: 'Body Text',
    description: 'Paragraph text color',
    group: 'core'
  },
  {
    key: '--bodyTextColorWhite',
    label: 'Body Text (White)',
    description: 'Light text on dark backgrounds',
    group: 'core'
  },
  {
    key: '--primaryDark',
    label: 'Primary Dark',
    description: 'Template dark accents and icon tile backgrounds',
    group: 'template'
  },
  {
    key: '--primaryDarker',
    label: 'Primary Deeper',
    description: 'Deeper template accent variant',
    group: 'template'
  },
  {
    key: '--secondaryDark',
    label: 'Secondary Dark',
    description: 'Dark secondary template accents',
    group: 'template'
  },
  {
    key: '--dark',
    label: 'Dark Surface',
    description: 'Dark mode and dark section surfaces',
    group: 'template'
  },
  {
    key: '--medium',
    label: 'Medium Surface',
    description: 'Medium dark mode surfaces',
    group: 'template'
  },
  {
    key: '--light',
    label: 'Light Surface',
    description: 'Light template surfaces',
    group: 'template'
  },
  {
    key: '--lightGrey',
    label: 'Light Grey',
    description: 'Soft grey template surfaces',
    group: 'template'
  },
  { key: '--accent', label: 'Accent', description: 'Template accent color', group: 'template' },
  {
    key: '--accentColor',
    label: 'Accent Color',
    description: 'Alternate template accent color',
    group: 'template'
  },
  {
    key: '--tertiary',
    label: 'Tertiary',
    description: 'Third template accent color',
    group: 'template'
  },
  {
    key: '--primaryShade',
    label: 'Primary Shade',
    description: 'Shaded primary template variant',
    group: 'template'
  },
  {
    key: '--secondaryShade',
    label: 'Secondary Shade',
    description: 'Shaded secondary template variant',
    group: 'template'
  },
  {
    key: '--darkPrimary',
    label: 'Dark Primary',
    description: 'Dark primary template variant',
    group: 'template'
  },
  {
    key: '--accessible',
    label: 'Accessible Accent',
    description: 'High-contrast template accent',
    group: 'template'
  },
  {
    key: '--accessible2',
    label: 'Accessible Accent 2',
    description: 'Alternate high-contrast template accent',
    group: 'template'
  },
  {
    key: '--primaryAccessible',
    label: 'Primary Accessible',
    description: 'Accessible primary template variant',
    group: 'template'
  },
  {
    key: '--headerColorWhite',
    label: 'Header White',
    description: 'Light heading text color',
    group: 'template'
  },
  {
    key: '--darkText',
    label: 'Dark Text',
    description: 'Template dark text color',
    group: 'template'
  }
]);

export const THEME_COLOR_KEYS = Object.freeze(THEME_COLOR_DEFS.map((def) => def.key));
export const THEME_COLOR_KEY_SET = Object.freeze(new Set(THEME_COLOR_KEYS));

/**
 * Canonical priority order for the core theme colour tokens. Single source of
 * truth for anything that must pick a deterministic winner among tokens that
 * share the same hex (in the defaults `--secondary` === `--primaryLight`):
 * the design swatch grid (`ui/design-drawers/_internals.js`) and iframe
 * StyleSnapshot token-identity resolution (Phase 3). Lives here in the shared
 * layer so both `ui/` and `editing/` can import it without a circular path.
 */
export const TOKEN_PRIORITY = Object.freeze([
  '--primary',
  '--secondary',
  '--primaryLight',
  '--secondaryLight',
  '--headerColor',
  '--bodyTextColor',
  '--bodyTextColorWhite'
]);

export const DEFAULT_THEME = Object.freeze({
  ...BASE_THEME_COLORS,
  '--topperFontSize': 'clamp(0.8125rem, 1.6vw, 1rem)',
  '--headerFontSize': 'clamp(1.9375rem, 3.9vw, 3.0625rem)',
  '--bodyFontSize': '1rem',
  '--sectionPadding': 'clamp(3.75rem, 7.82vw, 6.25rem) 1rem'
});

export function normalizeHexColor(value) {
  const trimmed = String(value ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed.match(/^#(.)(.)(.)$/);
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return '';
}

function parseHex(hex) {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16)
  ];
}

function toHexChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0');
}

function mixHex(hexA, hexB, weightB = 0.5) {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  if (!a || !b) return normalizeHexColor(hexA) || normalizeHexColor(hexB) || '#000000';
  const w = Math.max(0, Math.min(1, Number(weightB)));
  const mixed = a.map((channel, index) => channel * (1 - w) + b[index] * w);
  return `#${mixed.map(toHexChannel).join('')}`;
}

function valueOr(theme, key, fallback) {
  return normalizeHexColor(theme?.[key]) || fallback;
}

export function deriveExtendedThemeColors(theme = {}) {
  const primary = valueOr(theme, '--primary', BASE_THEME_COLORS['--primary']);
  const primaryLight = valueOr(theme, '--primaryLight', BASE_THEME_COLORS['--primaryLight']);
  const secondary = valueOr(theme, '--secondary', BASE_THEME_COLORS['--secondary']);
  const header = valueOr(theme, '--headerColor', BASE_THEME_COLORS['--headerColor']);
  const body = valueOr(theme, '--bodyTextColor', BASE_THEME_COLORS['--bodyTextColor']);
  const white = valueOr(theme, '--bodyTextColorWhite', BASE_THEME_COLORS['--bodyTextColorWhite']);

  return {
    '--primaryDark': mixHex(primary, '#000000', 0.72),
    '--primaryDarker': mixHex(primary, '#000000', 0.82),
    '--secondaryDark': mixHex(secondary, '#000000', 0.65),
    '--dark': mixHex(header, '#000000', 0.2),
    '--medium': mixHex(header, body, 0.45),
    '--light': white,
    '--lightGrey': mixHex(white, '#000000', 0.04),
    '--accent': secondary,
    '--accentColor': secondary,
    '--tertiary': primaryLight,
    '--primaryShade': mixHex(primary, '#000000', 0.45),
    '--secondaryShade': mixHex(secondary, '#000000', 0.45),
    '--darkPrimary': mixHex(primary, '#000000', 0.65),
    '--accessible': mixHex(primary, '#000000', 0.55),
    '--accessible2': mixHex(secondary, '#000000', 0.55),
    '--primaryAccessible': mixHex(primary, '#000000', 0.62),
    '--headerColorWhite': white,
    '--darkText': header
  };
}

/**
 * The theme a template load produces (IF-2b) — pure, so the leak scenario is
 * testable in node without the store.
 *
 * A payload carrying `mappedTheme` (its site palette deterministically mapped
 * to builder tokens at ingest) builds from a FRESH canonical base —
 * DEFAULT_THEME + mappedTheme — never from the prior project theme, whose keys
 * from a previously loaded template would leak into this one, and never
 * through the DEFAULT backfill alone, which is what painted `--primary
 * #ff6a3e` onto a site whose accent is `#60a5fa`. Templates WITHOUT
 * mappedTheme keep the legacy merge byte-identically: prior theme, overlaid
 * with the template's raw vars, backfilled by normalizeThemeTokens.
 *
 * @returns {{theme: object, mergedThemeVars: object}}
 */
export function deriveTemplateLoadTheme({ priorTheme = {}, template = {}, cssThemeVars = {} }) {
  const hasMappedTheme =
    template?.mappedTheme &&
    typeof template.mappedTheme === 'object' &&
    Object.keys(template.mappedTheme).length > 0;
  const theme = hasMappedTheme ? { ...DEFAULT_THEME, ...template.mappedTheme } : { ...priorTheme };
  const mergedThemeVars = { ...cssThemeVars, ...(template?.themeVars || {}) };
  for (const [key, value] of Object.entries(mergedThemeVars)) {
    theme[key] = typeof value === 'string' ? value.trim() : value;
  }
  Object.assign(theme, normalizeThemeTokens(theme, { preserveExisting: true }));
  return { theme, mergedThemeVars };
}

export function normalizeThemeTokens(
  theme = {},
  { preserveExisting = true, includeDerived = false } = {}
) {
  const input = theme && typeof theme === 'object' ? theme : {};
  const next = { ...DEFAULT_THEME, ...input };
  if (!includeDerived) return next;
  const derived = deriveExtendedThemeColors(next);
  for (const [key, value] of Object.entries(derived)) {
    if (!preserveExisting || next[key] == null || next[key] === '') {
      next[key] = value;
    }
  }
  return next;
}

export function expandPaletteColors(
  colors = {},
  { existingTheme = null, includeAllDerived = false } = {}
) {
  const base = colors && typeof colors === 'object' ? colors : {};
  const derived = deriveExtendedThemeColors(base);
  const next = { ...base };
  for (const [key, value] of Object.entries(derived)) {
    if (
      includeAllDerived ||
      (existingTheme && Object.prototype.hasOwnProperty.call(existingTheme, key))
    ) {
      next[key] = value;
    }
  }
  return {
    ...next
  };
}

/**
 * Is this colour usable as a brand primary/secondary?
 *
 * Rejects colours so light or so dark that a whole site keyed to them would be
 * unreadable — a near-white "brand colour" makes every button vanish.
 *
 * ONE PREDICATE, TWO CALLERS, AND THAT IS THE POINT. `deriveTheme` in
 * `ai/brand-skin.js` applies this at composition time and, when it fails,
 * substitutes the CodeStitch default orange. Any other code that elects a brand
 * colour must pre-filter with the SAME predicate, or it can hand `deriveTheme` a
 * colour it silently rejects — and a brand-import feature that quietly produces
 * foreign orange is the worst outcome that feature has.
 *
 * Lives here rather than in brand-skin.js because that module imports css-tree
 * as a bare specifier and is Node/Worker-only; this file has no imports at all.
 *
 * The formula is a plain weighted average of the 0–1 channels, NOT gamma-
 * corrected relative luminance. That is what the original implementation used,
 * and changing it would silently re-classify colours near the thresholds.
 */
export function isUsableBrandColor(value) {
  const rgb = parseHex(value);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.1 && luminance < 0.9;
}

/**
 * HSL helpers, on the 0–1 scale.
 *
 * Moved here verbatim from `ai/brand-skin.js`, where the whole cluster
 * (hexToRgb / rgbToHex / rgbToHsl / hslToRgb / lighten) existed to serve two
 * calls inside `deriveTheme`. The brand-kit planner needs the same maths — for
 * `--primaryLight` and for judging whether two elected colours are visually
 * distinct — and brand-skin.js cannot be imported from a browser module because
 * it pulls in css-tree as a bare specifier.
 *
 * Byte-for-byte identical to the original, deliberately: `--primaryLight` is
 * emitted into real projects, so a "tidier" rounding would silently change
 * every composed theme.
 */
export function hexToHsl(value) {
  const rgb = parseHex(value);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((channel) => channel / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h / 6, s, l };
}

export function hslToHex({ h, s, l }) {
  const channel = (value) => toHexChannel(value * 255);
  if (s === 0) return `#${channel(l)}${channel(l)}${channel(l)}`;
  const hue2rgb = (p, q, t) => {
    let next = t;
    if (next < 0) next += 1;
    if (next > 1) next -= 1;
    if (next < 1 / 6) return p + (q - p) * 6 * next;
    if (next < 1 / 2) return q;
    if (next < 2 / 3) return p + (q - p) * (2 / 3 - next) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return `#${channel(hue2rgb(p, q, h + 1 / 3))}${channel(hue2rgb(p, q, h))}${channel(hue2rgb(p, q, h - 1 / 3))}`;
}

/** Raise lightness by `amount`, clamped. Returns the input unchanged if unparseable. */
export function lightenHex(value, amount) {
  const hsl = hexToHsl(value);
  if (!hsl) return value;
  hsl.l = Math.max(0, Math.min(1, hsl.l + amount));
  return hslToHex(hsl);
}

/** Past this lightness a brand hue stops reading as the brand and reads as white. */
const TINT_MAX_LIGHTNESS = 0.92;
const TINT_LIGHTNESS_STEP = 0.02;

function ratioAgainst(hex, textRgb) {
  const rgb = parseHex(hex);
  if (!rgb || !textRgb) return Number.NaN;
  return contrastRatio({ r: rgb[0], g: rgb[1], b: rgb[2] }, textRgb);
}

/**
 * The LIGHT variant of a brand colour, as the `*Light` tokens are actually consumed.
 *
 * `lightenHex(base, 0.2)` encodes a relative bump, not the token's contract. Across
 * the section corpus `--primaryLight` / `--secondaryLight` are read 1,146 times as a
 * section BACKGROUND that the template's dark body text sits on, and 1,719 times as
 * accent text on a dark surface — both of which want a genuinely light value, which is
 * what the shipped defaults (`#ffba43`, `#ffd675`) are. A +0.2 bump cannot reach that
 * from a dark brand colour: uicompass's navy secondary derived to `#2d4478`, which
 * rendered `#side-by-side` at a contrast ratio of **1.14:1** against `--bodyTextColor`
 * in LIGHT mode. No gate saw it, because nothing measured the derived PAIR.
 *
 * So the bump is the floor, not the answer. When it already satisfies the contract the
 * lifted value is returned unchanged — which is why every brand that works today keeps
 * byte-identical output — and otherwise lightness climbs until the text is readable or
 * the hue runs out of headroom. Returning the lightest candidate on failure is
 * deliberate: a washed-out tint is a blemish, an unreadable section is a bug.
 *
 * @param {string} baseHex brand colour to derive from
 * @param {string} textHex the text colour that will sit on it (`--bodyTextColor`)
 * @param {{lift?: number, minRatio?: number}} [options]
 * @returns {string} hex, or the input unchanged when either colour is unparseable
 */
export function deriveLightTint(baseHex, textHex, { lift = 0.2, minRatio = 4.5 } = {}) {
  const lifted = lightenHex(baseHex, lift);
  const text = parseHex(textHex);
  if (!text) return lifted;
  const textRgb = { r: text[0], g: text[1], b: text[2] };
  // NaN-safe: an unparseable base keeps today's value rather than climbing blindly.
  if (!(ratioAgainst(lifted, textRgb) < minRatio)) return lifted;

  const hsl = hexToHsl(lifted);
  if (!hsl) return lifted;
  let best = lifted;
  for (let l = hsl.l + TINT_LIGHTNESS_STEP; l <= TINT_MAX_LIGHTNESS; l += TINT_LIGHTNESS_STEP) {
    best = hslToHex({ ...hsl, l });
    if (ratioAgainst(best, textRgb) >= minRatio) return best;
  }
  return best;
}

export function getThemeColorDef(key) {
  return THEME_COLOR_DEFS.find((def) => def.key === key) || null;
}

export function humanizeThemeToken(key) {
  const known = getThemeColorDef(key);
  if (known) return known.label;
  return String(key || '')
    .replace(/^--/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}
